import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MessageSquare, MoreVertical, GripVertical, FolderInput, Pin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Chat } from '@/services/chatService';
import { getChatUrl as buildChatUrl } from '@/lib/chatModeRoutes';
import { ChatFolder } from '@/services/folderService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

interface DraggableChatItemProps {
  chat: Chat;
  plan: string;
  folders?: ChatFolder[];
  onEditChat: (chat: Chat) => void;
  onDeleteChat: (chat: Chat) => void;
  onMoveToFolder?: (chat: Chat, folderId: string | null) => void;
  onTogglePin?: (chat: Chat) => void;
}

export function DraggableChatItem({
  chat,
  plan,
  folders = [],
  onEditChat,
  onDeleteChat,
  onMoveToFolder,
  onTogglePin,
}: DraggableChatItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: chat.id,
    data: {
      type: 'chat',
      chat,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getChatUrl = (c: Chat) => buildChatUrl(c.id, c.chat_mode);

  const chatUrl = getChatUrl(chat);
  const isActive = location.pathname === chatUrl;

  const handleClick = (e: React.MouseEvent) => {
    // Prevent navigation when dragging
    if (!isDragging) {
      navigate(chatUrl);
    }
  };

  const currentFolder = folders.find(f => f.id === chat.folder_id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex items-center cursor-grab active:cursor-grabbing ${isDragging ? 'z-50 shadow-lg' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div
        onClick={handleClick}
        className={`flex-1 flex items-start gap-2 pl-2 pr-8 py-2 rounded-lg transition-colors cursor-pointer ${
          isActive ? 'bg-secondary/10' : 'hover:bg-sidebar-accent'
        }`}
      >
        <GripVertical className="h-3 w-3 mt-1 text-muted-foreground/50 flex-shrink-0" />
        <div className="relative flex-shrink-0">
          <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-secondary" />
          {chat.is_pinned && (
            <Pin className="absolute -top-1 -right-1 h-2 w-2 text-primary fill-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-sidebar-foreground truncate" title={chat.title || '(Sin título)'}>
            {chat.title || '(Sin título)'}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {new Date(chat.updated_at).toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'short',
            })}
          </p>
        </div>
      </div>

      <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              onEditChat(chat);
            }}>
              ✏️ Editar título
            </DropdownMenuItem>
            
            {onTogglePin && (
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                onTogglePin(chat);
              }}>
                <Pin className={`h-3.5 w-3.5 mr-2 ${chat.is_pinned ? 'fill-current' : ''}`} />
                {chat.is_pinned ? 'Desfijar' : 'Fijar'}
              </DropdownMenuItem>
            )}
            
            {onMoveToFolder && folders.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <FolderInput className="h-3.5 w-3.5 mr-2" />
                  Mover a carpeta
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-44">
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveToFolder(chat, null);
                    }}
                    className={!chat.folder_id ? 'bg-accent' : ''}
                  >
                    📥 Sin categoría
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {folders.map((folder) => (
                    <DropdownMenuItem
                      key={folder.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveToFolder(chat, folder.id);
                      }}
                      className={chat.folder_id === folder.id ? 'bg-accent' : ''}
                    >
                      <span className="mr-2">{folder.icon}</span>
                      <span style={{ color: folder.color }}>{folder.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDeleteChat(chat);
              }}
              className="text-destructive focus:text-destructive"
            >
              🗑️ Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
